<template>
  <div class="chart-bars">
    <Bar :data="data" :options="data.options" :plugins="[movingAveragePlugin]" />
  </div>
</template>

<script lang="ts" setup>
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from 'chart.js'
import { Bar } from 'vue-chartjs'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import '@/service/service_months'
import { getSortedMonths, months } from '@/service/service_months'

const props = defineProps<{
  datasets: { backgroundColor: string; data: number[] }[]
  stepsize?: number
  yAxisLegend: string
  max?: number
  objectiveLine?: { value: number; label: string; color?: string }
  movingAverageLine?: number[]
}>()

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels)
ChartJS.defaults.datasets.bar.categoryPercentage = 0.5
ChartJS.defaults.datasets.bar.barPercentage = 1


const addBackgroundCategory = (chart: any) => {
  const { ctx, chartArea } = chart
  if (!chartArea) return

  ctx.save()
  ctx.fillStyle = '#0072ce30'

  const labelsCount = chart.data.labels.length
  const currentMonthIndex = new Date().getMonth()
  const cellWidth = (chartArea.right - chartArea.left) / labelsCount

  ctx.fillRect(
    chartArea.left + cellWidth * currentMonthIndex,
    chartArea.top,
    cellWidth,
    chartArea.bottom - chartArea.top
  )

  ctx.restore()
}

const drawObjectiveLine = (chart: any) => {
  if (!props.objectiveLine) return
  
  const { ctx, chartArea, scales } = chart
  if (!chartArea) return

  const yScale = scales.y
  const yPixel = yScale.getPixelForValue(props.objectiveLine.value)
  
  ctx.save()
  ctx.strokeStyle = props.objectiveLine.color || '#7bbf4a'
  ctx.lineWidth = 3
  ctx.setLineDash([8, 4])
  
  ctx.beginPath()
  ctx.moveTo(chartArea.left, yPixel)
  ctx.lineTo(chartArea.right, yPixel)
  ctx.stroke()
  
  ctx.font = 'bold 14px sans-serif'
  const textMetrics = ctx.measureText(props.objectiveLine.label)
  const textWidth = textMetrics.width
  const padding = 8
  const chipHeight = 24
  const chipX = chartArea.right - textWidth - padding * 2 - 10
  const chipY = yPixel - chipHeight / 2
  
  ctx.fillStyle = props.objectiveLine.color || '#7bbf4a'
  ctx.beginPath()
  ctx.roundRect(chipX, chipY, textWidth + padding * 2, chipHeight, 4)
  ctx.fill()
  
  ctx.fillStyle = '#ffffff'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(props.objectiveLine.label, chipX + (textWidth + padding * 2) / 2, chipY + chipHeight / 2)
  
  ctx.restore()
}

const movingAveragePlugin = {
  id: 'movingAverageLine',
  afterDatasetsDraw: (chart: any) => {
    if (!props.movingAverageLine || props.movingAverageLine.length === 0) return
    
    const hasNonZeroValues = props.movingAverageLine.some(value => value !== 0)
    if (!hasNonZeroValues) return
    
    const { ctx, chartArea, scales } = chart
    if (!chartArea) return

    const xScale = scales.x
    const yScale = scales.y
    const labelsCount = chart.data.labels.length
    const cellWidth = (chartArea.right - chartArea.left) / labelsCount
    const currentMonthIndex = new Date().getMonth()
    
    ctx.save()
    
    // Dessiner la ligne en segments avec opacité variable
    ctx.lineWidth = 6
    ctx.setLineDash([])
    
    for (let i = 0; i < props.movingAverageLine.length && i < labelsCount; i++) {
      const value = props.movingAverageLine[i]
      const nextValue = i < props.movingAverageLine.length - 1 ? props.movingAverageLine[i + 1] : null
      
      if (value !== 0 && nextValue !== null && nextValue !== 0) {
        const xPixel = chartArea.left + cellWidth * i + cellWidth / 2
        const yPixel = yScale.getPixelForValue(value)
        const nextXPixel = chartArea.left + cellWidth * (i + 1) + cellWidth / 2
        const nextYPixel = yScale.getPixelForValue(nextValue)
        
        // Rendre le segment très transparent s'il touche le mois actuel
        if (i === currentMonthIndex || i + 1 === currentMonthIndex) {
          ctx.strokeStyle = 'rgba(0, 114, 206, 0.2)' // Très transparent
        } else {
          ctx.strokeStyle = 'rgba(0, 114, 206, 0.7)' // Opacité normale
        }
        
        ctx.beginPath()
        ctx.moveTo(xPixel, yPixel)
        ctx.lineTo(nextXPixel, nextYPixel)
        ctx.stroke()
      }
    }
    
    // Dessiner les points avec un contour blanc pour plus de visibilité
    for (let i = 0; i < props.movingAverageLine.length && i < labelsCount; i++) {
      // Ne pas dessiner le point sur le mois actuel pour ne pas cacher le label
      if (i === currentMonthIndex) continue
      
      const value = props.movingAverageLine[i]
      if (value !== 0) {
        const xPixel = chartArea.left + cellWidth * i + cellWidth / 2
        const yPixel = yScale.getPixelForValue(value)
        
        // Contour blanc
        ctx.beginPath()
        ctx.fillStyle = '#ffffff'
        ctx.arc(xPixel, yPixel, 7, 0, 2 * Math.PI)
        ctx.fill()
        
        // Point bleu
        ctx.beginPath()
        ctx.fillStyle = '#0072ce'
        ctx.arc(xPixel, yPixel, 5, 0, 2 * Math.PI)
        ctx.fill()
      }
    }
    
    ctx.restore()
  }
}

const data = {
  labels: getSortedMonths(),
  datasets: props.datasets,
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: (context: any) => {
          const datasetIndex = context.datasetIndex
          const dataIndex = context.dataIndex
          const currentMonth = new Date().getMonth()
          return datasetIndex === 0 && dataIndex === currentMonth
        },
        align: 'end' as const,
        anchor: 'end' as const,
        color: '#fff',
        font: {
          weight: 'bold' as const,
          size: 18,
        },
        backgroundColor: '#0072ce',
        borderRadius: 3,
        padding: 4,
        zIndex: 1000,
        formatter: (value: number) => value.toLocaleString('fr-FR'),
      }
    },
    scales: {
      x: {
        grid: {
          color: '#0072ce20',
        },
        ticks: {
          color: '#0072ce',
          font: {
            size: 16,
            weight: 700,
          },
        },
      },
      y: {
        grid: {
          color: '#0072ce',
        },
        max: props.max,
        ticks: {
          color: '#0072ce',
          padding: 20,
          stepSize: props.stepsize,
          font: {
            weight: 700,
            size: 16,
          },
        },
        title: {
          display: true,
          text: props.yAxisLegend,
          color: '#0072ce',
          font: {
            weight: 700,
            size: 16
          },
        },
      },
    },
    animation: {
      onComplete: (chart: any) => {
        addBackgroundCategory(chart.chart)
        drawObjectiveLine(chart.chart)
      },
    },
  },
}

</script>

<style lang="scss" scoped>
.chart-bars {
  height: 100%;
}
</style>
